import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Output, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FonctionsService } from '../../services/utilities/fonctions.service';
import { VibrationsChart } from '../../models/charts/VibrationsChart';
import { VibrationsRecordService } from '../../services/records/vibrations-record.service';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { VibrationsChartChannel } from '../../models/charts/VibrationsChartChannel';
import { Chart, CategoryScale, LinearScale, Title, Tooltip, Legend, LineController, LineElement, PointElement, Filler } from 'chart.js';
import { CommonModule } from '@angular/common';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ProprieteAffichee } from '../../models/charts/ProprieteAffichee';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateModule } from '@ngx-translate/core';
import { VibrationRecords } from '@shared/models/vibrations-records';


@Component({
  selector: 'app-record-modal',
  imports: [CommonModule, AngularSvgIconModule, MatProgressBarModule, TranslateModule],
  templateUrl: './record-modal.component.html',
  styleUrl: './record-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordModalComponent {
  @ViewChild('fileInput') fileInput!: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<RecordModalComponent>,
    private recordSevice: VibrationsRecordService,
    private el: ElementRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdRef: ChangeDetectorRef

  ) {
    // Registering necessary components for Chart.js
    Chart.register(
      CategoryScale,
      LinearScale,
      Title,
      Tooltip,
      Legend,
      LineController,
      LineElement,
      PointElement,
      Filler,
    );
  }

  files: File[] = [];
  siteId: number;
  minMax: number[] = [];
  isRemoteOrLocal: boolean = false;
  indexSelected: number;
  fileTempSended: string = '';
  vibrationsChartLocal: VibrationsChart[] = [];
  vibrationsChart: VibrationsChart[] = [];
  tabDonneesVibrationNewModel: VibrationsChart[] = [];
  vibrationsNewModelSelected: VibrationsChart;
  fileName: string

  allRecords: VibrationsChart[] = [];
  proprietesAffichees: ProprieteAffichee[];
  showProgressbar = false;
  private chartInstances: Map<string, Chart> = new Map();


  confirm() {
    this.allRecords.forEach((record) => {
      if (this.data.controlPointName != null && record.controlPointName == null) {
        record.controlPointName = this.data.controlPointName;
      }
    });
    let data: VibrationRecords = {
      information: {
        currentURL: window.location.href,
        regionName: this.data.regionName,
        siteName: this.data.siteName,
        controlPointName: this.data.controlPointName,
      },
      vibrationRecords: this.allRecords
    }
    this.recordSevice.SaveRecords(this.data.siteId, data).subscribe((response) => {
      this.dialogRef.close(true);
    })
  }


  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  // Gérer le drop de fichier
  onFileDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input && input.files) {
      this.handleFiles(input.files);
    }
  }

  private handleFiles(files: FileList) {
    Array.from(files).forEach(file => {
      if (this.isFileAllowed(file.name)) {
        this.files.push(file);
      }

      setTimeout(() => {
        if (this.files.length > 0) {
          this.managerFile();
        } else {
        }
      }, 10 * files.length);

    });
  }

  private managerFile() {
    this.showProgressbar = true;
    this.vibrationsChartLocal = [];
    let formData = new FormData();

    for (let i = 0; i < this.files.length; i++) {
      formData.append('files', this.files[i], this.files[i].name);
    }

    this.recordSevice.UploadFilenRecords(this.data.siteId, formData).subscribe({
      next: (event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.Sent:
            break;
          case HttpEventType.ResponseHeader:
            this.isRemoteOrLocal = true;
            break;
          case HttpEventType.Response:
            let vib: VibrationsChart[] = event.body.data;
            this.allRecords = [];
            if (vib != null) {
              vib.forEach((v, i) => {
                this.chartListUpdate(v, i);
              })
            }
            this.fileTempSended = 'filesSended';
            setTimeout(() => {
              this.cdRef.markForCheck();
            }, 500);
        }
      },
      error: (error) => {
        setTimeout(() => {
        }, 500);
      },
      complete: () => {
      }
    });
  }

  public closeDialog(): void {
    this.dialogRef.close(false);
  }

  private chartListUpdate(vib: VibrationsChart, index: number) {
    this.vibrationsChart = this.vibrationsChartLocal;
    this.vibrationsNewModelSelected = vib;
    if (this.data.eventId) {
      this.vibrationsNewModelSelected.eventId = this.data.eventId;
    }
    if (this.data.controlPointId) {
      this.vibrationsNewModelSelected.controlPointId = this.data.controlPointId;
    }
    if (this.data.controlPointName) {
      this.vibrationsNewModelSelected.controlPointName = this.data.controlPointName;
    }
    this.allRecords.push(this.vibrationsNewModelSelected);
    this.indexSelected = 0;
    setTimeout(() => {
      this.drawingVibrations(index);
    }, 1000);

    setTimeout(() => {
      this.showProgressbar = false;
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
    }, 1000);
  }

  private checkChannelData(data: VibrationsChartChannel): boolean {
    let dataNullNumber = 0;
    data.timeSpectrum.values.forEach((v, i, a) => {
      if (v === 0) {
        dataNullNumber++;
      }
    });
    return dataNullNumber === data.timeSpectrum.values.length;
  }

  private isFileAllowed(fileName: string) {
    let isFileAllowed = false;
    const allowedFiles = ['.csv', '.sis', ''];
    const regex = /(?:\.([^.]+))?$/;
    const extension = regex.exec(fileName);
    if (undefined !== extension && null !== extension) {
      for (const ext of allowedFiles) {
        if (ext === extension[0]) {
          isFileAllowed = true;
        }
      }
    }
    return isFileAllowed;
  }

  private drawingVibrations(j: number) {
    if (this.allRecords.length === 0) return;
    let minMax = this.findMinMax(this.allRecords[j]);
    this.allRecords[j].signalDataEntity?.signalData?.channels.forEach((value, index, array) => {
      if (!this.checkChannelData(value)) {
        this.drawingChannel(index, this.allRecords[j], minMax, j);
      }
    })

  }

  private findMinMax(vibration: VibrationsChart): number[] {
    let minMax = [0, 0, 0, 0];
    vibration?.signalDataEntity?.signalData?.channels.forEach((value, index, array) => {
      if (index < vibration?.signalDataEntity?.signalData?.channels.length - 1) {
        if (value.timeSpectrum.ppvMin < minMax[0]) minMax[0] = value.timeSpectrum.ppvMin;
        if (value.timeSpectrum.ppvMax > minMax[1]) minMax[1] = value.timeSpectrum.ppvMax;
      } else {
        // this is last channel
        minMax[2] = value.timeSpectrum.ppvMin;
        minMax[3] = value.timeSpectrum.ppvMax;
      }
    })
    return minMax;
  }

  private drawingChannel(index: number, vibrations: VibrationsChart, minMax: number[], indexRecord: number) {
    let canvas = <HTMLCanvasElement>this.el.nativeElement.querySelector('#canvas-' + indexRecord + '-' + index);
    let ctx = canvas.getContext('2d');
    const chartKey = `${indexRecord}-${index}`;
    if (this.chartInstances.has(chartKey)) {
      this.chartInstances.get(chartKey)?.destroy();
      this.chartInstances.delete(chartKey);
  }

    let array_for_label: number[] = [];
    vibrations?.signalDataEntity?.signalData?.channels[index].timeSpectrum.values.forEach((value, index) => {
      array_for_label[index] = index;
    });
    let chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: array_for_label,
        datasets: [{
          label: 'Channel #' + (index + 1),
          data: vibrations?.signalDataEntity?.signalData?.channels[index].timeSpectrum.values,
          fill: false,
          tension: 0.1,
          borderWidth: 1,
          borderColor: 'rgb(153,0,77)',
          backgroundColor: 'white',
        }],
      },
      options: {
        elements: {
          point: {
            radius: 0
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
            position: 'bottom',
            labels: {
              boxWidth: 0
            },
          }
        },
        scales: {
          x: {
            grid: {
              tickLength: 3,
            },
            ticks: {
              maxTicksLimit: vibrations?.signalDataEntity?.signalData?.channels.length,
              callback: function (value, index, ticks) {
                return Math.round(index / 2048) + 's';
              },
              padding: 0
            },
            display: false,
          },
          y: {
            min: (index < vibrations?.signalDataEntity?.signalData?.channels.length - 1) ? minMax[0] : minMax[2],
            max: (index < vibrations?.signalDataEntity?.signalData?.channels.length - 1) ? minMax[1] : minMax[3],
            ticks: {
              stepSize: 0.5,
            },
            grid: {
              tickLength: 3,
            }
          }
        }
      }
    });
    this.chartInstances.set(chartKey, chart);
  }









  sortColumn: string = '';
  sortOrder: boolean = true;

  sortTable(column: string): void {

    if (this.sortColumn === column) {
      this.sortOrder = !this.sortOrder;
    } else {
      this.sortColumn = column;
      this.sortOrder = true;
    }


    /*  if (this.sortColumn === column) {
       this.sortOrder = !this.sortOrder; // Inverser l'ordre si la même colonne est triée à nouveau
     } else {
       this.sortColumn = column;
       this.sortOrder = true; // Tri croissant par défaut
     }
     this.files.sort((a, b) => {
       const valueA = a[column];
       const valueB = b[column];
 
       if (valueA instanceof Date && valueB instanceof Date) {
         // Comparaison des dates
         return this.sortOrder ? valueA.getTime() - valueB.getTime() : valueB.getTime() - valueA.getTime();
       } else {
         // Comparaison des autres types de données (chaînes de caractères)
         if (valueA < valueB) return this.sortOrder ? -1 : 1;
         if (valueA > valueB) return this.sortOrder ? 1 : -1;
         return 0;
       }
     }); */
  }

  findMaxAbsValue(min: number, max: number) {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }
}
